import { useState } from 'react';
import { useSelector } from 'react-redux';
import { DsVersionRef } from 'dragonmouse-shared/interfaces';
import Modal from 'components/Modal';
import { Tooltip } from 'components/Widget/Tooltip';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

interface VersionHistoryModalProps {
	onSelectVersion: (versionNumber: number) => void;
	onDuplicate: (versionNumber: number) => void;
	onRestore: (versionNumber: number) => void;
	onClose: () => void;
}

const formatRelativeTime = (date: Date): string => {
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMinutes = Math.floor(diffMs / 60_000);
	const diffHours = Math.floor(diffMs / 3_600_000);
	const diffDays = Math.floor(diffMs / 86_400_000);

	if (diffMinutes < 1) return 'just now';
	if (diffMinutes < 60) return `${diffMinutes}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < 30) return `${diffDays}d ago`;
	if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
	return `${Math.floor(diffDays / 365)}y ago`;
};

const formatFullDate = (date: Date): string => {
	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const VersionHistoryModal = ({
	onSelectVersion,
	onDuplicate,
	onRestore,
	onClose,
}: VersionHistoryModalProps) => {
	const dsVersionRefs = useSelector((r: RootReducer) => r.db.dsVersionRefs);
	const [tooltipId] = useState(`version-history-${Math.random()}`);

	return (
		<Modal title="Version History" onClose={onClose} maxWidth="340px">
			<S.List>
				{dsVersionRefs.map((v: DsVersionRef) => (
					<S.VersionRow key={v.versionNumber}>
						<S.RowLeft>
							<S.VersionLabel>v{v.versionNumber}</S.VersionLabel>
							<S.RelativeDate
								data-tooltip-id={tooltipId}
								data-tooltip-content={formatFullDate(v.createdAt)}
							>
								{formatRelativeTime(v.createdAt)}
							</S.RelativeDate>
						</S.RowLeft>
						<S.RowRight>
							<S.ActionLink onClick={() => onSelectVersion(v.versionNumber)}>Load</S.ActionLink>
							<S.ActionLink onClick={() => onDuplicate(v.versionNumber)}>Duplicate</S.ActionLink>
							<S.ActionLink onClick={() => onRestore(v.versionNumber)}>Restore</S.ActionLink>
						</S.RowRight>
					</S.VersionRow>
				))}
			</S.List>
			<Tooltip id={tooltipId} placement="left" />
		</Modal>
	);
};

export default VersionHistoryModal;
